import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth, API, formatApiErrorDetail } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  DotsThree,
  Users,
  EnvelopeSimple,
  ShieldCheck
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const roleLabels = {
  super_admin: 'Super Admin',
  org_admin: 'Org Admin',
  dept_admin: 'Dept Admin',
  campaign_manager: 'Campaign Manager',
  editor: 'Editor',
  viewer: 'Viewer',
  data_analyst: 'Data Analyst'
};

const roleColors = {
  super_admin: 'bg-purple-100 text-purple-700',
  org_admin: 'bg-blue-100 text-blue-700',
  dept_admin: 'bg-teal-100 text-teal-700',
  campaign_manager: 'bg-amber-100 text-amber-700',
  editor: 'bg-emerald-100 text-emerald-700',
  viewer: 'bg-zinc-100 text-zinc-600',
  data_analyst: 'bg-pink-100 text-pink-700'
};

export function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'viewer',
    department_ids: [],
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [usersRes, deptsRes] = await Promise.all([
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/departments`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/users`, formData, { withCredentials: true });
      toast.success('User created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updateData = { ...formData };
      delete updateData.email;
      delete updateData.password;
      await axios.put(`${API}/users/${editingUser.id}`, updateData, { withCredentials: true });
      toast.success('User updated successfully');
      setEditingUser(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    try {
      await axios.delete(`${API}/users/${id}`, { withCredentials: true });
      toast.success('User deactivated successfully');
      fetchData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to deactivate user');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'viewer',
      department_ids: [],
      is_active: true
    });
  };

  const openEditModal = (targetUser) => {
    setFormData({
      email: targetUser.email,
      password: '',
      name: targetUser.name,
      role: targetUser.role,
      department_ids: targetUser.department_ids || [],
      is_active: targetUser.is_active
    });
    setEditingUser(targetUser);
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(search.toLowerCase()) ||
                          u.email.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getDepartmentNames = (deptIds) => {
    if (!deptIds || deptIds.length === 0) return '-';
    return deptIds.map(id => departments.find(d => d.id === id)?.name || id).join(', ');
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-zinc-900">Users</h1>
            <p className="text-zinc-500 mt-1">Manage user accounts and permissions</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary"
            data-testid="create-user-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create User
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-swiss"
              data-testid="user-search"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]" data-testid="role-filter">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {Object.entries(roleLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-zinc-100 animate-pulse rounded-sm" />
            ))}
          </div>
        ) : filteredUsers.length > 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="table-header text-left">User</th>
                    <th className="table-header text-left">Role</th>
                    <th className="table-header text-left">Departments</th>
                    <th className="table-header text-center">Status</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((targetUser) => (
                    <tr key={targetUser.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`user-row-${targetUser.email}`}>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-sm bg-[#002FA7] flex items-center justify-center text-white font-semibold">
                            {targetUser.name?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <p className="font-medium text-zinc-900">{targetUser.name}</p>
                            <p className="text-sm text-zinc-500">{targetUser.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`${roleColors[targetUser.role]} px-2 py-1 rounded-sm text-xs font-medium`}>
                          {roleLabels[targetUser.role]}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-zinc-600 text-sm max-w-xs truncate">
                        {getDepartmentNames(targetUser.department_ids)}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${targetUser.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {targetUser.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-2 hover:bg-zinc-100 rounded-sm" data-testid={`user-menu-${targetUser.email}`}>
                              <DotsThree className="w-5 h-5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditModal(targetUser)}>
                              <PencilSimple className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {targetUser.role !== 'super_admin' && (
                              <DropdownMenuItem onClick={() => handleDelete(targetUser.id)} className="text-red-600">
                                <Trash className="w-4 h-4 mr-2" />
                                Deactivate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-white border border-zinc-200 rounded-sm">
            <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No users found</p>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal || !!editingUser} onOpenChange={() => {
          setShowCreateModal(false);
          setEditingUser(null);
          resetForm();
        }}>
          <DialogContent className="modal-brutalist sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                {editingUser ? 'Edit User' : 'Create User'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={editingUser ? handleUpdate : handleCreate}>
              <div className="space-y-4 py-4">
                {!editingUser && (
                  <>
                    <div>
                      <Label className="label-uppercase">Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-swiss"
                        placeholder="user@example.com"
                        required
                        data-testid="user-email-input"
                      />
                    </div>
                    <div>
                      <Label className="label-uppercase">Password</Label>
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="input-swiss"
                        placeholder="Create a password"
                        required
                        minLength={6}
                        data-testid="user-password-input"
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label className="label-uppercase">Full Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input-swiss"
                    placeholder="John Doe"
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div>
                  <Label className="label-uppercase">Role</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(v) => setFormData({ ...formData, role: v })}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(roleLabels).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-uppercase">Departments</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto p-2 border border-zinc-200 rounded-sm">
                    {departments.map(dept => (
                      <label key={dept.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.department_ids.includes(dept.id)}
                          onChange={(e) => {
                            const newIds = e.target.checked
                              ? [...formData.department_ids, dept.id]
                              : formData.department_ids.filter(id => id !== dept.id);
                            setFormData({ ...formData, department_ids: newIds });
                          }}
                          className="rounded border-zinc-300"
                        />
                        <span className="text-sm">{dept.name}</span>
                      </label>
                    ))}
                    {departments.length === 0 && (
                      <p className="text-sm text-zinc-500">No departments available</p>
                    )}
                  </div>
                </div>
                {editingUser && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-zinc-300"
                    />
                    <Label htmlFor="is_active" className="text-sm font-normal cursor-pointer">
                      User is active
                    </Label>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => {
                  setShowCreateModal(false);
                  setEditingUser(null);
                  resetForm();
                }}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="user-save-btn">
                  {saving ? 'Saving...' : (editingUser ? 'Update' : 'Create User')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
